import { Context } from './context'

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export type AdditionalParams = { error?: unknown } & { [key: string]: unknown }

export interface LogMessage {
  level: LogLevel
  module: string
  msg: string
  params?: AdditionalParams
}

export interface LoggerBackend {
  isLevelEnabled(level: LogLevel): boolean
  write(message: LogMessage): void
}

export class Logger {
  private static backend?: LoggerBackend

  static installBackend(backend: LoggerBackend) {
    Logger.backend = backend
  }

  constructor(private module: string) {}

  get isDebugEnabled(): boolean {
    return Logger.backend?.isLevelEnabled(LogLevel.DEBUG) ?? false
  }

  get isInfoEnabled(): boolean {
    return Logger.backend?.isLevelEnabled(LogLevel.INFO) ?? false
  }

  get isWarnEnabled(): boolean {
    return Logger.backend?.isLevelEnabled(LogLevel.WARN) ?? false
  }

  get isErrorEnabled(): boolean {
    return Logger.backend?.isLevelEnabled(LogLevel.ERROR) ?? false
  }

  private write(ctx: Context, level: LogLevel, msg: string | (() => string), params?: AdditionalParams) {
    const backend = Logger.backend
    if (!backend || !backend.isLevelEnabled(level)) return
    const message = typeof msg === 'string' ? msg : msg()
    backend.write({
      level,
      module: this.module,
      msg: message,
      params: {
        ...params,
        traceId: ctx.traceId,
        userId: ctx.userId,
      },
    })
  }

  debug(ctx: Context, msg: string | (() => string), params?: AdditionalParams): void {
    this.write(ctx, LogLevel.DEBUG, msg, params)
  }

  info(ctx: Context, msg: string | (() => string), params?: AdditionalParams): void {
    this.write(ctx, LogLevel.INFO, msg, params)
  }

  warn(ctx: Context, msg: string | (() => string), params?: AdditionalParams): void {
    this.write(ctx, LogLevel.WARN, msg, params)
  }

  error(ctx: Context, msg: string | (() => string), params?: AdditionalParams): void {
    this.write(ctx, LogLevel.ERROR, msg, params)
  }
}
